import { EditOutlined, LoginOutlined, LogoutOutlined } from "@ant-design/icons";
import { useContext, useMemo, useState } from "react";
import { AllUsersContext, GroupContext } from "../../lib/contexts";
import { SettingsContentContainer } from "./settingsContentContainer";
import { ConfirmModal } from "../elements/confirmModal";
import { MIconButton } from "../elements/miconButton";
import { MButton } from "../elements/mbutton";
import { MModal } from "../elements/mmodal";
import { PinInput } from "../general/pinInput";
import { MInput } from "../elements/mInputs";
import { BsPeopleFill } from "react-icons/bs";
import { Avatar } from "../general/Avatar";
import { callEndpoint } from "../../lib/functions";
import {
  JoinGroupEndpoint,
  LeaveGroupEndpoint,
  UpdateGroupEndpoint,
} from "@ckal-software/ckal-lib/dist/apps/madplanen";
import { useNotifcation } from "../../lib/hooks/useNotification";

export function GroupContent() {
  const [groupId, setGroupId] = useState(-1);
  const [newGroupName, setNewGroupName] = useState("");
  const [isChangingGroupName, setIsChangingGroupName] = useState(false);
  const [showLeaveGroupConfirm, setShowLeaveGroupConfirm] = useState(false);
  const [showJoinGroupPopup, setShowJoinGroupPopup] = useState(false);
  const [showChangeGroupNamePopup, setShowChangeGroupNamePopup] =
    useState(false);

  const { group, fetchMyGroup } = useContext(GroupContext);
  const allUsers = useContext(AllUsersContext);
  const notify = useNotifcation();

  const members = useMemo(
    () =>
      (group?.members || []).map((m) => allUsers.find((u) => u.email === m)),
    [allUsers, group],
  );

  if (!group) {
    return null;
  }

  return (
    <div>
      <SettingsContentContainer title={group.name}>
        <div style={{ marginTop: "24px", fontSize: "18px" }}>
          Gruppe-ID: <code>{group.shortId}</code>
        </div>
        <div style={{ marginTop: "24px", display: "grid", rowGap: 8 }}>
          <h3>Medlemmer</h3>
          {members.map((member, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                columnGap: 8,
                alignItems: "center",
              }}
            >
              <Avatar
                email={member?.email || ""}
                firstname={member?.firstname || ""}
                lastname={member?.lastname || ""}
                size={32}
              />
              <div style={{ fontSize: "16px" }}>{member?.fullName}</div>
            </div>
          ))}
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: "24px",
          }}
        >
          <MIconButton
            title="Forlad gruppe"
            type="danger"
            icon={<LogoutOutlined style={{ transform: "scaleX(-1)" }} />}
            onClick={() => setShowLeaveGroupConfirm(true)}
          />
          <MIconButton
            title="Skift gruppenavn"
            icon={<EditOutlined />}
            style={{ marginRight: "auto" }}
            onClick={() => setShowChangeGroupNamePopup(true)}
          />
          <MButton
            type="primary"
            icon={<LoginOutlined />}
            onClick={() => {
              setGroupId(-1);
              setShowJoinGroupPopup(true);
            }}
          >
            Skift gruppe
          </MButton>
        </div>
      </SettingsContentContainer>
      <ConfirmModal
        visible={showLeaveGroupConfirm}
        title="Forlad gruppe"
        message={
          <>
            Er du sikker på, at du vil forlade gruppen{" "}
            <strong>{group.name}</strong>?
          </>
        }
        onCancel={() => setShowLeaveGroupConfirm(false)}
        confirmButton={
          <MButton
            type="danger"
            icon={<LogoutOutlined style={{ transform: "scaleX(-1)" }} />}
            onClick={async () => {
              const [err] = await callEndpoint(LeaveGroupEndpoint, undefined);

              if (err) {
                notify(`Forlad gruppe: Der skete en fejl: ${err.error}`);
                return;
              }

              await fetchMyGroup();
              setShowLeaveGroupConfirm(false);
            }}
          >
            Forlad gruppe
          </MButton>
        }
      />
      <MModal
        visible={showChangeGroupNamePopup}
        onClose={() => setShowChangeGroupNamePopup(false)}
        title="Skift gruppenavn"
        hideCloseIcon
        content={
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "24px",
              }}
            >
              <BsPeopleFill style={{ fontSize: "120px" }} />
            </div>
            <MInput
              placeholder="Nyt gruppenavn"
              value={newGroupName}
              autoFocus
              onChange={(e) => setNewGroupName(e.target.value)}
            />
          </div>
        }
        footer={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <MButton
              type="ghost"
              onClick={() => setShowChangeGroupNamePopup(false)}
              style={{ marginRight: "8px" }}
            >
              Annuller
            </MButton>
            <MButton
              type="primary"
              icon={<EditOutlined />}
              onClick={async () => {
                const [err] = await callEndpoint(
                  UpdateGroupEndpoint,
                  { name: newGroupName },
                  { setWorking: setIsChangingGroupName },
                );

                if (err) {
                  notify("Ændring af gruppenavn: Der skete en fejl");
                  return;
                }

                await fetchMyGroup();
                setShowChangeGroupNamePopup(false);
              }}
              loading={isChangingGroupName}
              disabled={!newGroupName}
            >
              Skift navn
            </MButton>
          </div>
        }
      />
      <MModal
        visible={showJoinGroupPopup}
        onClose={() => setShowJoinGroupPopup(false)}
        title="Skift gruppe"
        hideCloseIcon
        content={
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 24,
              }}
            >
              <BsPeopleFill style={{ fontSize: 120 }} />
            </div>
            <div style={{ marginBottom: 8 }}>Gruppe-ID:</div>
            <PinInput onPinChange={setGroupId} />
          </div>
        }
        footer={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <MButton type="ghost" onClick={() => setShowJoinGroupPopup(false)}>
              Annuller
            </MButton>
            <MButton
              type="primary"
              icon={<LoginOutlined />}
              disabled={groupId.toString().length !== 6}
              onClick={async () => {
                const [err, , { response }] = await callEndpoint(
                  JoinGroupEndpoint,
                  { shortId: groupId },
                );

                if (err) {
                  notify(
                    response?.status === 404
                      ? `Gruppe med Gruppe-ID ${groupId} eksisterer ikke`
                      : "Skift gruppe: Der skete en fejl",
                  );
                  return;
                }

                await fetchMyGroup();
                setShowJoinGroupPopup(false);
              }}
            >
              Skift
            </MButton>
          </div>
        }
      />
    </div>
  );
}
